import { Area, Bar, BarChart, CartesianGrid, Cell, ComposedChart, LabelList, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, EmptyState } from './Card.js';
import { fmtIls, fmtMonth, fmtPct } from '../utils.js';
import { useChartAnimation } from '../printScope.js';
import { SERIES, bySign, GRID, AXIS, TOOLTIP_PROPS, VOLUME_GRADIENT, PCT_AXIS_WIDTH_TITLED, MONEY_AXIS_WIDTH_TITLED, pctAxisDomain } from '../chartTheme.js';

export interface PortfolioMonth {
  date: string;
  total_volume: number;
  monthly_return: number | null;
  cumulative_return: number;
  benchmark_monthly: number | null;
  benchmark_cumulative: number;
}

export interface PortfolioYearStat {
  year: string;
  portfolio: number;
  benchmark: number | null;
}

/**
 * גרף תשואה כולל לתיקים המנוהלים — the Excel summary chart: navy cumulative line,
 * red cumulative benchmark, monthly-change bars, yearly תשואה/בנצ'מרק callouts.
 */
export const portfolioReturnHasData = (months: PortfolioMonth[]) => months.filter((m) => m.monthly_return !== null).length >= 2;

export function PortfolioReturnChart({ months, years }: { months: PortfolioMonth[]; years: PortfolioYearStat[] }) {
  const animate = useChartAnimation();
  const data = months.filter((m) => m.monthly_return !== null);
  if (data.length < 2) return <Card title="גרף תשואה כולל"><EmptyState label="אין מספיק נתונים" /></Card>;

  return (
    <Card title="גרף תשואה כולל לתיקים המנוהלים">
      {/* Two stacked panels sharing the x-axis: monthly bars on top, cumulative lines below */}
      <ResponsiveContainer width="100%" height={140}>
        <ComposedChart data={data} syncId="portfolio-return" margin={{ top: 4, right: 8, left: 8, bottom: 20 }}>
          <CartesianGrid {...GRID} />
          <XAxis
            dataKey="date"
            tickFormatter={fmtMonth}
            {...AXIS}
            minTickGap={40}
            label={{ value: 'תאריך', position: 'insideBottom', offset: -12, fill: 'var(--foreground)', fontSize: 12 }}
          />
          <YAxis
            orientation="left"
            width={PCT_AXIS_WIDTH_TITLED}
            tickFormatter={(v: number) => fmtPct(v, 0)}
            {...AXIS}
            domain={pctAxisDomain(data.map((d) => d.monthly_return))}
            label={{ value: 'שינוי חודשי (%)', angle: -90, position: 'insideLeft', fill: 'var(--foreground)', fontSize: 12 }}
          />
          <Tooltip
            {...TOOLTIP_PROPS}
            formatter={(v: number) => [fmtPct(v), 'שינוי חודשי']}
            labelFormatter={fmtMonth}
          />
          <Bar dataKey="monthly_return" radius={[2, 2, 0, 0]} name="monthly_return" isAnimationActive={animate}>
            {data.map((d) => (
              <Cell key={d.date} fill={bySign(d.monthly_return ?? 0)} fillOpacity={0.75} />
            ))}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={data} syncId="portfolio-return" margin={{ top: 4, right: 8, left: 8, bottom: 20 }}>
          <CartesianGrid {...GRID} />
          <XAxis
            dataKey="date"
            tickFormatter={fmtMonth}
            {...AXIS}
            minTickGap={40}
            label={{ value: 'תאריך', position: 'insideBottom', offset: -12, fill: 'var(--foreground)', fontSize: 12 }}
          />
          <YAxis
            orientation="left"
            tickFormatter={(v: number) => fmtPct(v, 0)}
            {...AXIS}
            width={PCT_AXIS_WIDTH_TITLED}
            domain={pctAxisDomain(data.flatMap((d) => [d.cumulative_return, d.benchmark_cumulative]))}
            label={{ value: 'תשואה מצטברת (%)', angle: -90, position: 'insideLeft', fill: 'var(--foreground)', fontSize: 12 }}
          />
          <Tooltip
            {...TOOLTIP_PROPS}
            formatter={(v: number, name: string) =>
              [fmtPct(v), name === 'cumulative_return' ? 'שינוי מצטבר' : "מדד ייחוס מצטבר"]}
            labelFormatter={fmtMonth}
          />
          <Line type="monotone" dataKey="cumulative_return" stroke={SERIES.portfolio} strokeWidth={2.5} dot={false} name="cumulative_return" isAnimationActive={animate} />
          <Line type="monotone" dataKey="benchmark_cumulative" stroke={SERIES.benchmark} strokeWidth={2} dot={false} name="benchmark_cumulative" isAnimationActive={animate} />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="mt-2 flex justify-center gap-6 text-xs text-muted-foreground">
        <span><span className="me-1 inline-block h-1 w-4 rounded align-middle" style={{ background: SERIES.portfolio }} />שינוי מצטבר</span>
        <span><span className="me-1 inline-block h-1 w-4 rounded align-middle" style={{ background: SERIES.benchmark }} />מדד ייחוס מצטבר</span>
        <span><span className="me-1 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: SERIES.positive }} />חודש חיובי</span>
        <span><span className="me-1 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: SERIES.negative }} />חודש שלילי</span>
      </div>
      {years.length > 0 && (
        <div className="mt-4 border-t border-border pt-4">
          <h3 className="mb-2 text-sm text-muted-foreground">תשואה שנתית מול מדד ייחוס</h3>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={years} barGap={2} margin={{ top: 4, right: 8, left: 8, bottom: 20 }}>
              <CartesianGrid {...GRID} vertical={false} />
              <XAxis
                dataKey="year"
                {...AXIS}
                label={{ value: 'שנה', position: 'insideBottom', offset: -12, fill: 'var(--foreground)', fontSize: 12 }}
              />
              <YAxis
                orientation="right"
                tickFormatter={(v: number) => fmtPct(v, 0)}
                {...AXIS}
                width={PCT_AXIS_WIDTH_TITLED}
                domain={pctAxisDomain(years.flatMap((y) => [y.portfolio, y.benchmark]))}
                label={{ value: 'תשואה (%)', angle: 90, position: 'insideRight', fill: 'var(--foreground)', fontSize: 12 }}
              />
              <Tooltip
                {...TOOLTIP_PROPS}
                formatter={(v: number, name: string) => [fmtPct(v), name === 'portfolio' ? 'תשואה' : 'מדד ייחוס']}
              />
              <Bar dataKey="portfolio" name="portfolio" radius={[3, 3, 0, 0]} isAnimationActive={animate}>
                {years.map((y) => (
                  <Cell key={y.year} fill={bySign(y.portfolio)} />
                ))}
                <LabelList dataKey="portfolio" position="top" formatter={(v: number) => fmtPct(v, 1)} style={{ fill: 'var(--foreground)', fontSize: 10 }} />
              </Bar>
              <Bar dataKey="benchmark" name="benchmark" fill={SERIES.benchmark} fillOpacity={0.7} radius={[3, 3, 0, 0]} isAnimationActive={animate}>
                <LabelList dataKey="benchmark" position="top" formatter={(v: number | null) => (v === null ? '' : fmtPct(v, 1))} style={{ fill: 'var(--foreground)', fontSize: 10 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-2 flex justify-center gap-6 text-xs text-muted-foreground">
            <span><span className="me-1 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: SERIES.positive }} />שנה חיובית</span>
            <span><span className="me-1 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: SERIES.negative }} />שנה שלילית</span>
            <span><span className="me-1 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ background: SERIES.benchmark }} />מדד ייחוס</span>
          </div>
        </div>
      )}

    </Card>
  );
}

/** סה"כ נכסים פיננסים — total financial assets bars over time. */
export const totalAssetsHasData = (months: PortfolioMonth[]) => months.some((m) => m.total_volume > 0);

export function TotalAssetsChart({ months }: { months: PortfolioMonth[] }) {
  const animate = useChartAnimation();
  const data = months.filter((m) => m.total_volume > 0);
  if (data.length === 0) return <Card title={'סה"כ נכסים פיננסים'}><EmptyState label="אין נתונים" /></Card>;

  return (
    <Card title={'סה"כ נכסים פיננסים'}>
      <ResponsiveContainer width="100%" height={310}>
        <ComposedChart data={data} margin={{ top: 4, right: 8, left: 8, bottom: 20 }}>
          <defs>
            <linearGradient id="assets" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={VOLUME_GRADIENT.from} stopOpacity={0.9} />
              <stop offset="100%" stopColor={VOLUME_GRADIENT.to} stopOpacity={0.9} />
            </linearGradient>
          </defs>
          <CartesianGrid {...GRID} />
          <XAxis
            dataKey="date"
            tickFormatter={fmtMonth}
            {...AXIS}
            minTickGap={40}
            label={{ value: 'תאריך', position: 'insideBottom', offset: -12, fill: 'var(--foreground)', fontSize: 12 }}
          />
          <YAxis
            orientation="right"
            tickFormatter={(v: number) => `${Math.round(v / 1e6)}M`}
            {...AXIS}
            width={MONEY_AXIS_WIDTH_TITLED}
            label={{ value: 'סה"כ נכסים (מיליון ₪)', angle: 90, position: 'insideRight', fill: 'var(--foreground)', fontSize: 12 }}
          />
          <Tooltip
            {...TOOLTIP_PROPS}
            formatter={(v: number) => [fmtIls(v), 'יתרה']}
            labelFormatter={fmtMonth}
          />
          <Bar dataKey="total_volume" fill="url(#assets)" radius={[2, 2, 0, 0]} isAnimationActive={animate} />
          <Area type="monotone" dataKey={() => null} />
        </ComposedChart>
      </ResponsiveContainer>
    </Card>
  );
}
